/**
 * AIF-03 — Step 3: analysis overview (dry-run — nothing written yet) (§11.5).
 *
 * Renders the AnalyzeReport: totals line; per-class table (matched name+count;
 * unmatched flagged "will be SKIPPED" INLINE, never popups); unmatched images;
 * malformed lines (file+line); missing-dims; a confidence slider + numeric
 * field (1–100%, lower-bounded at file min — typing lower shows an info hint and
 * clamps); and a 4-option deletion-scope radio with live would-soft-delete /
 * kept-converted / dedupe counts. Changing the floor or scope re-runs analyze
 * (debounced) so the previews stay live.
 *
 * The floor + scope live HERE (lifted to the parent via onTuningChange) so step
 * 4 executes with exactly what the overview previewed.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type {
  AiFindingsDeletionScope,
  AnalyzeAiFindingsImportReport,
  ImportLabelFile,
} from "@aeriosiq/types";
import { clampPercent, floorPercent, percentToFraction } from "./confidence.js";
import { DELETION_SCOPE_OPTIONS } from "./labels.js";
import { recomputePerClassCounts } from "./recomputeCounts.js";

export interface Step3Tuning {
  /** Integer percent 1..100. */
  confidencePercent: number;
  deletionScope: AiFindingsDeletionScope;
}

export interface Step3AnalyzeProps {
  report: AnalyzeAiFindingsImportReport | null;
  isAnalyzing: boolean;
  error: string | null;
  tuning: Step3Tuning;
  onTuningChange: (next: Step3Tuning) => void;
  /**
   * ANA-R1-15 — the raw label files already held by the wizard. Used to
   * recompute the per-class count column client-side as the confidence slider
   * moves (no re-analyze round-trip). When omitted, the table falls back to the
   * report's pre-floor `perClass[].count` (prior behaviour).
   */
  files?: readonly ImportLabelFile[];
}

const DEBOUNCE_MS = 300;

export function Step3Analyze({
  report,
  isAnalyzing,
  error,
  tuning,
  onTuningChange,
  files,
}: Step3AnalyzeProps): ReactElement {
  const fileMin = report?.confidence.fileMin ?? null;
  const minPercent = floorPercent(fileMin);

  // ANA-R1-15 — recompute the per-class counts client-side at the CURRENT
  // threshold from the in-memory label files (the same keep rule as the BE
  // import: confidence >= floor). Memoised on files + the live percent so a
  // slider drag updates the column instantly with no network. When no files
  // are supplied, the lookup stays empty and the table falls back to the
  // report's pre-floor counts.
  const recomputedCounts = useMemo(
    () =>
      files
        ? recomputePerClassCounts(
            files,
            percentToFraction(tuning.confidencePercent),
          )
        : null,
    [files, tuning.confidencePercent],
  );

  // Local text-field value so typing isn't fought by the debounced parent push.
  const [fieldText, setFieldText] = useState(String(tuning.confidencePercent));
  const [clampHint, setClampHint] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setFieldText(String(tuning.confidencePercent));
  }, [tuning.confidencePercent]);

  // When a new report arrives with a higher file-min than the current floor,
  // bump the floor up so the control never sits below the (new) lower bound.
  useEffect(() => {
    if (tuning.confidencePercent < minPercent) {
      onTuningChange({ ...tuning, confidencePercent: minPercent });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minPercent]);

  function pushPercent(requested: number): void {
    const { value, clamped } = clampPercent(requested, fileMin);
    setClampHint(clamped);
    onTuningChange({ ...tuning, confidencePercent: value });
  }

  function onFieldChange(text: string): void {
    setFieldText(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const n = Number(text);
      if (text.trim() !== "" && Number.isFinite(n)) pushPercent(n);
    }, DEBOUNCE_MS);
  }

  function onSlider(percent: number): void {
    setClampHint(false);
    onTuningChange({ ...tuning, confidencePercent: percent });
  }

  function onScope(scope: AiFindingsDeletionScope): void {
    onTuningChange({ ...tuning, deletionScope: scope });
  }

  if (isAnalyzing && !report) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress aria-label="Analyzing" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" data-testid="analyze-error">
        {error}
      </Alert>
    );
  }

  if (!report) {
    return (
      <Typography variant="body2" color="text.secondary">
        Analysis has not run yet.
      </Typography>
    );
  }

  const { totals, images, perClass, malformedLines, missingDims, confidence } =
    report;
  const matchedClasses = perClass.filter((c) => c.matched);
  const unmatchedClasses = perClass.filter((c) => !c.matched);

  // ANA-R1-15 — the count shown for a class row: the client-side recomputed
  // count at the current threshold when files are available (live with the
  // slider), else the report's pre-floor count (fallback / prior behaviour).
  const classCount = (c: { classIndex: number; count: number }): number =>
    recomputedCounts ? (recomputedCounts.get(c.classIndex) ?? 0) : c.count;

  const unmatchedDetections = unmatchedClasses.reduce(
    (sum, c) => sum + classCount(c),
    0,
  );
  const kept = totals.importable;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        opacity: isAnalyzing ? 0.6 : 1,
      }}
    >
      {/* Totals line */}
      <Typography variant="body1" data-testid="analyze-totals">
        {`Files ${String(
          images.matched + images.unmatched.length,
        )} · detections ${String(totals.detections)} · matched images ${String(
          images.matched,
        )} · unmatched ${String(images.unmatched.length)}`}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        {/* Per-class table */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Per class
          </Typography>
          <Table size="small" aria-label="Per-class detection counts">
            <TableHead>
              <TableRow>
                <TableCell>Class</TableCell>
                <TableCell align="right">Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {matchedClasses.map((c) => (
                <TableRow key={`m-${String(c.classIndex)}`}>
                  <TableCell>{c.className}</TableCell>
                  <TableCell
                    align="right"
                    data-testid={`class-count-${String(c.classIndex)}`}
                  >
                    {classCount(c)}
                  </TableCell>
                </TableRow>
              ))}
              {unmatchedClasses.map((c) => (
                <TableRow key={`u-${String(c.classIndex)}`}>
                  <TableCell>
                    <Typography variant="body2" color="warning.main">
                      {`#${String(c.classIndex)} unknown — will be SKIPPED`}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="right"
                    data-testid={`class-count-${String(c.classIndex)}`}
                  >
                    {classCount(c)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Inline issues (never popups) */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Issues
          </Typography>
          {unmatchedClasses.length > 0 && (
            <Alert severity="warning" data-testid="issue-unmatched-class">
              {`${String(unmatchedClasses.length)} unknown class ${
                unmatchedClasses.length === 1 ? "index" : "indices"
              } (${unmatchedClasses
                .map((c) => `#${String(c.classIndex)}`)
                .join(", ")}) → ${String(
                unmatchedDetections,
              )} detections will be SKIPPED`}
            </Alert>
          )}
          {images.unmatched.length > 0 && (
            <Alert severity="warning" data-testid="issue-unmatched-images">
              {`${String(
                images.unmatched.length,
              )} txt without image match → skipped (${String(
                totals.skippedNoImage,
              )} detections)`}
            </Alert>
          )}
          {malformedLines.length > 0 && (
            <Alert severity="warning" data-testid="issue-malformed">
              <Typography variant="body2" component="div">
                {`${String(malformedLines.length)} malformed line${
                  malformedLines.length === 1 ? "" : "s"
                }:`}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {malformedLines.slice(0, 10).map((ml, i) => (
                  <li key={`${ml.file}:${String(ml.line)}:${String(i)}`}>
                    <Typography variant="caption">
                      {`${ml.file} line ${String(ml.line)} — ${ml.reason}`}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Alert>
          )}
          {missingDims.length > 0 && (
            <Alert severity="warning" data-testid="issue-missing-dims">
              {`${String(
                missingDims.length,
              )} image${missingDims.length === 1 ? "" : "s"} missing pixel dims → skipped (${String(
                totals.skippedNoDims,
              )} detections)`}
            </Alert>
          )}
          {unmatchedClasses.length === 0 &&
            images.unmatched.length === 0 &&
            malformedLines.length === 0 &&
            missingDims.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No issues detected.
              </Typography>
            )}
        </Box>
      </Box>

      {/* Confidence threshold */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {`Confidence ≥ ${String(tuning.confidencePercent)}%`}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Slider
            value={tuning.confidencePercent}
            min={minPercent}
            max={100}
            step={1}
            onChange={(_e, v) => {
              onSlider(Array.isArray(v) ? (v[0] ?? minPercent) : v);
            }}
            aria-label="Confidence threshold percent"
            sx={{ maxWidth: 320 }}
          />
          <TextField
            size="small"
            type="number"
            label="%"
            value={fieldText}
            onChange={(e) => {
              onFieldChange(e.target.value);
            }}
            slotProps={{
              htmlInput: {
                min: 1,
                max: 100,
                "aria-label": "Confidence threshold percent field",
              },
            }}
            sx={{ width: 96 }}
          />
          <Typography variant="body2" color="text.secondary">
            {confidence.fileMin !== null
              ? `file min ${String(floorPercent(confidence.fileMin))}%`
              : "no detections"}
          </Typography>
          <Typography variant="body2" data-testid="kept-count">
            {`→ ${String(kept)} kept`}
          </Typography>
        </Box>
        {clampHint && (
          <Alert severity="info" sx={{ mt: 1 }} data-testid="clamp-hint">
            {`The threshold can't go below the file minimum (${String(
              minPercent,
            )}%) — the files were already pre-thresholded.`}
          </Alert>
        )}
      </Box>

      {/* Deletion scope */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Existing findings — deletion scope
        </Typography>
        <FormControl>
          <RadioGroup
            value={tuning.deletionScope}
            onChange={(e) => {
              onScope(e.target.value as AiFindingsDeletionScope);
            }}
            aria-label="Deletion scope"
          >
            {DELETION_SCOPE_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2">{opt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {opt.hint}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>
        <Typography variant="body2" sx={{ mt: 1 }} data-testid="scope-preview">
          {`→ soft-deletes ${String(
            report.wouldSoftDelete ?? 0,
          )} · keeps ${String(
            report.wouldKeepConverted ?? 0,
          )} (already converted) · dedupes ${String(
            report.wouldDedupe ?? 0,
          )} incoming`}
        </Typography>
      </Box>
    </Box>
  );
}
